import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import type { GuaranteeConflict } from '../../api/leaseRent'

/** The rule label in the viewer's language, then English, then its code. */
export function conflictLabel(c: GuaranteeConflict, lang: string): string {
  return c.label?.[lang.slice(0, 2)] ?? c.label?.en ?? c.code
}

/**
 * A 409 guarantee_conflict (spec 2026-10-25-08 step 8): the law voids the
 * guarantor's commitment rather than forbidding the record, so the manager is
 * warned with the rule and may record it anyway, which is logged.
 */
export function GuaranteeConflictConfirmDialog({ conflicts, onConfirm, onCancel }: {
  conflicts: GuaranteeConflict[]
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t, i18n } = useTranslation('contracts')
  return (
    <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent data-testid="guarantee-conflict-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('lease.guarantees.conflict_title')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              {conflicts.map((c) => (
                <div key={c.rule_uid}>
                  <p>{conflictLabel(c, i18n.language)}</p>
                  {c.legal_basis && <p className="text-xs text-muted-foreground">{c.legal_basis}</p>}
                </div>
              ))}
              <p>{t('lease.guarantees.conflict_help')}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="guarantee-conflict-cancel-button">{t('lease.guarantees.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="guarantee-conflict-confirm-button">{t('lease.guarantees.conflict_confirm')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
